import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';
import { FiLock, FiCheckCircle, FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';
import logo from '../assets/logo-auth.png';
import './Auth.css';

/**
 * Reset Password Page
 *
 * ARCHITECTURE: This project uses AWS-based authentication.
 * Password reset is handled by our backend, NOT Supabase.
 *
 * Flow:
 * 1. User clicks reset link in email (contains token)
 * 2. This page validates token with backend
 * 3. User enters new password
 * 4. Backend validates token, updates password, invalidates all sessions
 * 5. Redirect to login (role-aware)
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [resetComplete, setResetComplete] = useState(false);

  // Get token from URL query params
  const token = searchParams.get('token');

  // Check if this is admin reset flow
  const isAdminFlow = location.pathname.startsWith('/admin');
  const loginPath = isAdminFlow ? '/admin/login' : '/login';
  const forgotPath = isAdminFlow ? '/admin/forgot-password' : '/forgot-password';

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidatingToken(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await authAPI.validateResetToken(token);
        if (response.data.success && response.data.valid) {
          setTokenValid(true);
          setTokenData(response.data);
        } else {
          setTokenValid(false);
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setTokenValid(false);
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  // Password validation
  const validatePassword = () => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validatePassword();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.resetPassword(token, password);

      if (response.data.success) {
        setResetComplete(true);
        toast.success('Password updated successfully!');

        // Redirect to login after delay
        setTimeout(() => {
          navigate(loginPath);
        }, 3000);
      } else {
        toast.error(response.data.message || 'Failed to update password');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update password. Please try again.';
      toast.error(message);

      // If token is invalid/expired, show the invalid state
      if (error.response?.status === 400) {
        setTokenValid(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state while validating token
  if (validatingToken) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <img src={logo} alt="Hisab OK Hai" className="auth-logo" />
            <h1>Hisab OK Hai</h1>
          </div>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
            <p>Verifying your reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid/expired token or no token
  if (!tokenValid) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <img src={logo} alt="Hisab OK Hai" className="auth-logo" />
            <h1>Hisab OK Hai</h1>
          </div>
          <div style={{ textAlign: 'center', padding: 20 }}>
            <FiAlertCircle size={64} color="#E74C3C" style={{ marginBottom: 20 }} />
            <h2>Link Expired or Invalid</h2>
            <p style={{ color: '#7F8C8D', marginBottom: 24 }}>
              {!token
                ? 'No reset token provided. Please request a new password reset.'
                : 'This password reset link has expired or is invalid. Please request a new reset link.'}
            </p>
            <Link to={forgotPath} className="btn btn-primary btn-block">
              Request New Link
            </Link>
            <Link to={loginPath} className="btn btn-link" style={{ marginTop: 16, display: 'block' }}>
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state after password reset
  if (resetComplete) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <img src={logo} alt="Hisab OK Hai" className="auth-logo" />
            <h1>Hisab OK Hai</h1>
          </div>
          <div style={{ textAlign: 'center', padding: 20 }}>
            <FiCheckCircle size={64} color="#27AE60" style={{ marginBottom: 20 }} />
            <h2>Password Updated!</h2>
            <p style={{ color: '#7F8C8D', marginBottom: 24 }}>
              Your password has been successfully updated.
              <br />
              Redirecting to login...
            </p>
            <Link to={loginPath} className="btn btn-primary btn-block">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src={logo} alt="Hisab OK Hai" className="auth-logo" />
          <h1>Hisab OK Hai</h1>
          <p className="tagline">Set your new password</p>
        </div>

        <h2>Reset Password</h2>
        <p style={{ color: '#7F8C8D', marginBottom: 24, fontSize: '0.9rem' }}>
          Enter your new password below.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">
              <FiLock style={{ marginRight: 8, verticalAlign: 'middle' }} />
              New Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter new password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#7F8C8D',
                }}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              <FiLock style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
            />
          </div>

          <div className="password-requirements" style={{
            fontSize: '0.8rem',
            color: '#7F8C8D',
            marginBottom: 16,
            padding: '12px',
            backgroundColor: '#F8F9FA',
            borderRadius: 4,
          }}>
            <strong>Password Requirements:</strong>
            <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
              <li style={{ color: password.length >= 8 ? '#27AE60' : '#7F8C8D' }}>
                At least 8 characters
              </li>
              <li style={{ color: /[A-Z]/.test(password) ? '#27AE60' : '#7F8C8D' }}>
                One uppercase letter
              </li>
              <li style={{ color: /[a-z]/.test(password) ? '#27AE60' : '#7F8C8D' }}>
                One lowercase letter
              </li>
              <li style={{ color: /[0-9]/.test(password) ? '#27AE60' : '#7F8C8D' }}>
                One number
              </li>
              <li style={{ color: password && password === confirmPassword ? '#27AE60' : '#7F8C8D' }}>
                Passwords match
              </li>
            </ul>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        <div className="auth-footer" style={{ marginTop: 24 }}>
          <Link to={loginPath} className="btn btn-link">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
